"use client";

import type React from "react";
import { useState, useEffect } from "react";
import {
  VStack,
  FormControl,
  FormLabel,
  Input,
  Button,
  HStack,
  Box,
} from "@chakra-ui/react";
import type { PostCallAnalysisData } from "./PostCallAnalysis.tsx";
import { useAppTheme } from "../../../ThemeContext.tsx";

interface BooleanAnalysisFormProps {
  onSave: (data: PostCallAnalysisData) => void;
  onCancel: () => void;
  initialData?: PostCallAnalysisData;
}

export const BooleanAnalysisForm: React.FC<BooleanAnalysisFormProps> = ({
  onSave,
  onCancel,
  initialData,
}) => {
  const [name, setName] = useState(initialData?.name || "");
  const [description, setDescription] = useState(
    initialData?.description || ""
  );
  const { resolvedTheme } = useAppTheme();
  const isDark = resolvedTheme === "dark";

  useEffect(() => {
    if (initialData) {
      setName(initialData.name);
      setDescription(initialData.description);
    }
  }, [initialData]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      type: "boolean",
      name,
      description,
    });
  };

  return (
    <Box mb={4}>
      <form onSubmit={handleSubmit}>
        <VStack spacing={4} align="stretch">
          <FormControl isRequired>
            <FormLabel fontSize="sm">Name</FormLabel>
            <Input
              id="boolean-analysis-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="appointment_booked"
              size="sm"
              _placeholder={{ color: isDark ? "gray.400" : "gray.600" }}
              textColor={isDark ? "white" : "gray.800"}
              bg={isDark ? "gray.700" : "white"}
              color={isDark ? "white" : "gray.800"}
              borderColor={isDark ? "gray.600" : "gray.300"}
              _hover={{ borderColor: isDark ? "gray.500" : "gray.400" }}
              variant="outline"
            />
          </FormControl>

          <FormControl isRequired>
            <FormLabel fontSize="sm">Description</FormLabel>
            <Input
              id="boolean-analysis-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Did the user book an appointment?"
              size="sm"
              _placeholder={{ color: isDark ? "gray.400" : "gray.600" }}
              textColor={isDark ? "white" : "gray.800"}
              bg={isDark ? "gray.700" : "white"}
              color={isDark ? "white" : "gray.800"}
              borderColor={isDark ? "gray.600" : "gray.300"}
              _hover={{ borderColor: isDark ? "gray.500" : "gray.400" }}
              variant="outline"
            />
          </FormControl>

          <HStack justify="flex-end" spacing={2}>
            <Button
              variant="ghost"
              size="sm"
              onClick={onCancel}
              colorScheme={isDark ? "black" : "white"}
              textColor={isDark ? "whiteAlpha" : "gray.800"}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              colorScheme="gray"
              size="sm"
              _hover={{ bg: isDark ? "gray.600" : "gray.300" }}
              background={isDark ? "gray.700" : "gray.200"}
              textColor={isDark ? "whiteAlpha" : "gray.800"}
            >
              {initialData ? "Update" : "Save"}
            </Button>
          </HStack>
        </VStack>
      </form>
    </Box>
  );
};
